import React from 'react';
import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { Verified, FlashOn } from '@mui/icons-material';
import img from '../../../../assets/ar/banner1.jpeg';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '70vh', sm: '65vh', md: '82vh' },
        minHeight: { xs: 480, md: 560 },
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#000',
      }}
    >
      {/* Background Image */}
      <Box
        component={motion.div}
        initial={{ scale: 1.08, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: 'easeOut' }}
        sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      >
        <Box
          component="img"
          src={img}
          alt="ar-infotech banner"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.65,
          }}
        />
      </Box>

      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0, width: '100%', height: '100%',
          background: 'linear-gradient(120deg, rgba(0,20,60,0.85) 0%, rgba(0,60,140,0.55) 45%, rgba(0,150,200,0.15) 100%)',
        }}
      />

      {/* Animated Floating Orbs */}
      <Box
        component={motion.div}
        animate={{ y: [-18, 18, -18], x: [-10, 10, -10] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute', top: '10%', right: '8%',
          width: { xs: 120, md: 280 }, height: { xs: 120, md: 280 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,210,196,0.25) 0%, rgba(14,106,218,0.1) 60%, transparent 100%)',
          filter: 'blur(20px)',
        }}
      />
      <Box
        component={motion.div}
        animate={{ y: [15, -15, 15], x: [10, -10, 10] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        sx={{
          position: 'absolute', bottom: '12%', right: '20%',
          width: { xs: 80, md: 180 }, height: { xs: 80, md: 180 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100,180,255,0.2) 0%, transparent 70%)',
          filter: 'blur(16px)',
        }}
      />

      <Container maxWidth={false} sx={{ position: 'relative', zIndex: 1, maxWidth: { xs: '100%', md: '1340px' } }}>
        <Grid container>
          <Grid item xs={12} md={8}>
            <Box
              component={motion.div}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Badge */}
              <motion.div variants={itemVariants}>
                <Chip
                  icon={<Verified sx={{ color: '#00D2C4 !important', fontSize: '1rem' }} />}
                  label="100% Genuine • Verified Seller"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(0,210,196,0.15)',
                    border: '1px solid rgba(0,210,196,0.4)',
                    color: '#00D2C4',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', md: '0.85rem' },
                    backdropFilter: 'blur(8px)',
                    px: 1
                  }}
                />
              </motion.div>

              {/* Main Heading */}
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  sx={{
                    color: '#FFF',
                    fontSize: { xs: '2.4rem', sm: '3rem', md: '4.8rem' },
                    lineHeight: 1.1,
                    mb: 2,
                    fontWeight: 900,
                    letterSpacing: '-0.5px',
                    textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                  }}
                >
                  Genuine Microsoft <br />
                  <Box
                    component={motion.span}
                    animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                    sx={{
                      background: 'linear-gradient(90deg, #00D2C4, #87CEEB, #00D2C4)',
                      backgroundSize: '200% auto',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Software Licenses
                  </Box>
                </Typography>
              </motion.div>

              {/* Subtext */}
              <motion.div variants={itemVariants}>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.88)',
                    fontWeight: 400,
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    mb: 4,
                    maxWidth: '550px',
                    fontFamily: '"Inter", sans-serif',
                    lineHeight: 1.75,
                    textShadow: '0 1px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  Get 100% original product keys for Windows OS, Microsoft Office, and Windows Server with instant e-delivery, secure payments, and lifetime support.
                </Typography>
              </motion.div>

              {/* Buttons */}
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    component={motion.a}
                    whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(14,106,218,0.5)' }}
                    whileTap={{ scale: 0.97 }}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: { xs: 3, md: 5 },
                      py: 1.6,
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      fontWeight: 700,
                      borderRadius: '50px',
                      background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                      boxShadow: '0 4px 20px rgba(14,106,218,0.4)',
                    }}
                    href="/shop"
                  >
                    Shop Licenses
                  </Button>
                  <Button
                    component={motion.a}
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.12)' }}
                    whileTap={{ scale: 0.97 }}
                    variant="outlined"
                    size="large"
                    sx={{
                      color: '#FFF',
                      borderColor: 'rgba(255,255,255,0.6)',
                      borderWidth: '2px',
                      px: { xs: 3, md: 5 },
                      py: 1.6,
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      fontWeight: 700,
                      borderRadius: '50px',
                      backdropFilter: 'blur(8px)',
                      '&:hover': {
                        borderColor: '#FFF',
                        borderWidth: '2px',
                      },
                    }}
                    href="/services"
                  >
                    Our Services
                  </Button>
                </Box>
              </motion.div>

              {/* Quick Trust Badges */}
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap' }}>
                  {[
                    { icon: <FlashOn sx={{ fontSize: '1rem' }} />, text: 'Instant Delivery' },
                    { icon: <Verified sx={{ fontSize: '1rem' }} />, text: 'Lifetime Activation' },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255,255,255,0.75)' }}>
                      {item.icon}
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
